import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import HOC from '../../components/HOC/HOC';
import styles from '../../css/style.module.css';
import page from './WithdrawalWindow.module.css';
import { getApi, putApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import img2 from '../../assest/loading1.gif';

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

const getCachedUserType = () => {
  try {
    const cached = JSON.parse(localStorage.getItem('adminData') || '{}');
    return cached?.data?.userType || cached?.userType || '';
  } catch {
    return '';
  }
};

const pad = value => String(value).padStart(2, '0');

const utcIsoToIstInput = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const ist = new Date(date.getTime() + IST_OFFSET_MS);
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
};

const formatIstLabel = iso => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return `${new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date)} IST`;
};

const istInputToPayload = value => (value ? value.replace('T', ' ') : '');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MESSAGE_LIMIT = 500;

const formatPreviewClock = value => {
  if (!value || !value.includes('T')) return '';
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) return '';
  const period = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 || 12;
  return `${day} ${MONTHS[month - 1]} ${year}, ${hour12}:${pad(minute)} ${period}`;
};

const buildStaffMessage = (startValue, endValue, customMessage) => {
  const startLabel = formatPreviewClock(startValue);
  const endLabel = formatPreviewClock(endValue);
  if (!startLabel || !endLabel) return '';

  const timeSentence = `Withdrawals are temporarily unavailable from ${startLabel} to ${endLabel} IST due to a technical issue at the bank's end. Please try again after this period. We apologize for the inconvenience.`;
  const custom = (customMessage || '').trim();
  return custom ? `${custom} ${timeSentence}` : timeSentence;
};

const getWindowState = windowData => {
  if (!windowData?.startAt || !windowData?.endAt) return 'unset';
  if (!windowData.isActive) return 'off';
  const now = Date.now();
  const start = new Date(windowData.startAt).getTime();
  const end = new Date(windowData.endAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 'unset';
  if (now >= start && now <= end) return 'blocking';
  if (now < start) return 'scheduled';
  return 'expired';
};

const STATUS_COPY = {
  unset: {
    label: 'Not set',
    detail: 'No downtime window is saved. Staff withdrawals are allowed.',
  },
  off: {
    label: 'Turned off',
    detail: 'Dates are saved, but the window is off. Staff withdrawals are allowed.',
  },
  blocking: {
    label: 'Blocking now',
    detail: 'The current time is inside this window. Staff withdrawals are blocked.',
  },
  scheduled: {
    label: 'Scheduled',
    detail: 'The window is on, and withdrawals will be blocked when it starts.',
  },
  expired: {
    label: 'Outside window',
    detail: 'The window is on, but the current time is outside it. Staff withdrawals are allowed.',
  },
};

const WithdrawalWindow = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [windowData, setWindowData] = useState(null);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (getCachedUserType() === 'SUB_ADMIN') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const applyWindow = data => {
    const record = data && data.startAt ? data : null;
    setWindowData(record);
    setStartAt(utcIsoToIstInput(record?.startAt));
    setEndAt(utcIsoToIstInput(record?.endAt));
    setIsActive(record ? Boolean(record.isActive) : true);
    setMessage(typeof record?.message === 'string' ? record.message : '');
  };

  const fetchWindow = async () => {
    await getApi(endPoints.getWithdrawalWindow, {
      setResponse: response => applyWindow(response?.data),
      setLoading,
      errorMsg: 'Failed to fetch withdrawal window!',
    });
  };

  useEffect(() => {
    if (getCachedUserType() === 'SUB_ADMIN') return;
    fetchWindow();
  }, []);

  const handleSave = async () => {
    if (!startAt || !endAt) {
      toast.error('Start and end date and time are required');
      return;
    }
    if (startAt >= endAt) {
      toast.error('Withdrawal window start must be before the end');
      return;
    }
    if (message.length > MESSAGE_LIMIT) {
      toast.error('Withdrawal window message must be 500 characters or fewer');
      return;
    }

    const response = await putApi(
      endPoints.updateWithdrawalWindow,
      {
        startAt: istInputToPayload(startAt),
        endAt: istInputToPayload(endAt),
        isActive,
        message,
      },
      {
        setLoading: setSaving,
        successMsg: isActive
          ? 'Withdrawal window updated. Withdrawals are blocked inside this window.'
          : 'Withdrawal window updated. The window is turned off, so withdrawals are allowed.',
        errorMsg: 'Failed to update withdrawal window!',
      }
    );

    if (response?.data) applyWindow(response.data);
  };

  const state = getWindowState(windowData);
  const status = STATUS_COPY[state];
  const previewMessage = buildStaffMessage(startAt, endAt, message);

  return (
    <div className={styles.jobmanagementCotainer}>
      <div className={styles.jobtoppart}>
        <div className={styles.pagetitle}>
          <h6>Withdrawal Window</h6>
        </div>
        <div className={styles.rightbuttons}>
          <div className={styles.rightbuttons2} onClick={handleSave}>
            <p>{saving ? 'Saving...' : 'Save'}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingnormal}>
          <img src={img2} alt="Loading" width={100} height={100} />
        </div>
      ) : (
        <div className={styles.jobchildcontainer}>
          <div className={`${page.status} ${page[state]}`}>
            <p className={page.statusLabel}>{status.label}</p>
            <p className={page.statusDetail}>{status.detail}</p>
            {windowData?.startAt && (
              <p className={page.statusRange}>
                {formatIstLabel(windowData.startAt)} to {formatIstLabel(windowData.endAt)}
              </p>
            )}
          </div>

          <div className={styles.addcontactdetails}>
            <div className={styles.addjobinputs}>
              <label htmlFor="withdrawal-start">Start (IST)*</label>
              <input
                id="withdrawal-start"
                type="datetime-local"
                value={startAt}
                onChange={e => setStartAt(e.target.value)}
              />
            </div>
            <div className={styles.addjobinputs}>
              <label htmlFor="withdrawal-end">End (IST)*</label>
              <input
                id="withdrawal-end"
                type="datetime-local"
                value={endAt}
                onChange={e => setEndAt(e.target.value)}
              />
            </div>
            <div className={styles.addjobinputs}>
              <label htmlFor="withdrawal-active">Window</label>
              <select
                id="withdrawal-active"
                value={isActive ? 'true' : 'false'}
                onChange={e => setIsActive(e.target.value === 'true')}
              >
                <option value="true">On — block withdrawals inside this window</option>
                <option value="false">Off — allow withdrawals</option>
              </select>
            </div>
          </div>
          <div className={styles.addjobinputs}>
            <label htmlFor="withdrawal-message">Staff message</label>
            <textarea
              id="withdrawal-message"
              placeholder="Optional note shown before the time window. Leave blank to show only the time message."
              value={message}
              maxLength={MESSAGE_LIMIT}
              onChange={e => setMessage(e.target.value)}
            />
            <span className={page.counter}>
              {message.length}/{MESSAGE_LIMIT}
            </span>
          </div>
          <div className={page.preview}>
            <p className={page.previewTitle}>Staff message preview</p>
            <p className={page.previewBody}>
              {previewMessage || 'Enter a start and end time to preview the message staff will see.'}
            </p>
            <p className={page.previewNote}>
              {isActive
                ? 'Staff see this when they try to withdraw while the current time is inside the window.'
                : 'This preview is saved with the window. Staff will not see it until the window is turned on.'}
            </p>
          </div>
          <p className={page.note}>
            Enter start and end as India time. A custom message is shown first, then the time-window
            sentence is added after it.
          </p>
        </div>
      )}
    </div>
  );
};

export default HOC(WithdrawalWindow);
